import { Copy, Radio } from 'lucide-react';
import { useMemo, useState } from 'react';
import { api } from '../api/client';
import { duration } from '../lib/format';
import { useLoad } from '../lib/useLoad';
import { LoadIssue } from '../components/LoadIssue';

interface Series {
  labels: Record<string, string>;
  value: number;
}
interface MetricFamily {
  name: string;
  help: string;
  type: string;
  /** Capped by the server to what this screen draws. */
  series: Series[];
  /** How many series the family really has — `series` is a prefix of them. */
  total?: number;
}
interface MetricsStatus {
  scrape: {
    count: number;
    lastAt: string | null;
    ageMs: number | null;
    lastFrom: string | null;
    lastAgent: string | null;
    medianIntervalMs: number | null;
    samples: number;
  };
  endpoint: { path: string; boundTo: string; note: string };
  series: MetricFamily[];
  totalSeries: number;
}

/**
 * The export, and — the part that matters — whether anything is reading it.
 *
 * Grafana already draws these numbers, so redrawing them here would be a worse
 * copy of a better tool. What Grafana CANNOT tell you is that Prometheus stopped
 * scraping: it is downstream of the gap, so its panels simply freeze at their
 * last value and keep looking calm. Prometheus cannot tell you either, being the
 * thing that stopped. The exporter is the only place that knows, so that is what
 * this screen leads with.
 */
const STATUS_WORDS: Record<number, string> = {
  0: 'ok',
  1: 'warn',
  2: 'down',
  3: 'unknown',
  4: 'skipped',
};

export function Metrics() {
  const [copied, setCopied] = useState(false);
  const { data, error, loading, updatedAt, reload } = useLoad(
    () => api.get<MetricsStatus>('/metrics-status'),
    [],
    15_000,
  );

  const scrapeConfig = useMemo(
    () =>
      [
        'scrape_configs:',
        '  - job_name: sphere-watch',
        '    scrape_interval: 30s',
        '    static_configs:',
        `      - targets: ['${data?.endpoint.boundTo ?? '127.0.0.1:4300'}']`,
        '        labels:',
        '          env: prod',
        '          service: sphere-watch',
      ].join('\n'),
    [data],
  );

  if (loading) return <div className="wt-empty">Loading…</div>;
  if (!data) return <LoadIssue error={error} updatedAt={null} what="the export status" onRetry={() => void reload()} />;

  const { scrape } = data;
  const never = scrape.count === 0;
  const stale = scrape.ageMs !== null && scrape.ageMs > 5 * 60_000;

  return (
    <>
      <div className="wt-head">
        <h1>Metrics</h1>
        <span className="sub">
          {data.series.length} families · {data.totalSeries} series
        </span>
      </div>
      <LoadIssue error={error} updatedAt={updatedAt} what="the export status" onRetry={() => void reload()} />

      {never && (
        <div className="wt-banner wt-banner-warn">
          <strong>Nothing has scraped /metrics.</strong> Any Grafana panel built on this export is empty
          or frozen, and no alert rule over it is evaluating. Add the job below to the Prometheus on the
          VPS.
        </div>
      )}
      {stale && (
        <div className="wt-banner wt-banner-danger">
          <strong>No scrape for {duration(scrape.ageMs ?? 0)}.</strong> Grafana is still drawing the last
          values it received — those panels look calm and are not being updated.
        </div>
      )}

      <div className="wt-table-wrap">
        <table className="wt-table wt-kv" style={{ marginBottom: 20 }}>
          <tbody>
            <tr>
              <th>
                <Radio size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
                Last scrape
              </th>
              <td className="wt-mono">
                {never ? 'never' : `${duration(scrape.ageMs ?? 0)} ago`}
                {scrape.lastFrom ? ` · from ${scrape.lastFrom}` : ''}
              </td>
            </tr>
            <tr>
              <th>Observed interval</th>
              <td className="wt-mono">
                {/* The interval Prometheus is ACTUALLY using, measured here, rather
                    than whatever its config claims — those drift. */}
                {scrape.medianIntervalMs
                  ? `${duration(scrape.medianIntervalMs)} (median of ${scrape.samples})`
                  : 'not enough samples yet'}
              </td>
            </tr>
            <tr>
              <th>Scrapes this process</th>
              <td className="wt-mono">{scrape.count}</td>
            </tr>
            <tr>
              <th>Scraper</th>
              <td className="wt-mono">{scrape.lastAgent ?? '—'}</td>
            </tr>
            <tr>
              <th>Endpoint</th>
              <td className="wt-mono">
                {data.endpoint.boundTo}
                {data.endpoint.path}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="wt-audit-payload-head">
        <span>Scrape config for the existing Prometheus</span>
        <span className="wt-spacer" />
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => {
            void navigator.clipboard.writeText(scrapeConfig);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
          }}
        >
          <Copy size={13} /> {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="wt-audit-payload" style={{ marginBottom: 8 }}>
        {scrapeConfig}
      </pre>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 0, marginBottom: 24 }}>
        {data.endpoint.note} If Prometheus runs in a container, join it to <code>sphere_default</code>{' '}
        and target <code>sphere_watch:4300</code> rather than publishing the port.
      </p>

      <div className="wt-head" style={{ marginBottom: 8 }}>
        <h1 style={{ fontSize: 15 }}>Currently exported</h1>
        <span className="sub">
          a gap means <strong>unknown</strong> or <strong>skipped</strong> — the series is removed, not
          held at its last value
        </span>
      </div>

      {data.series.map((family) => (
        <section key={family.name} style={{ marginBottom: 14 }}>
          <div className="wt-daygroup" style={{ marginTop: 0 }}>
            {family.name} <span style={{ textTransform: 'none', fontWeight: 400 }}>· {family.help}</span>
          </div>
          {family.series.length === 0 ? (
            <div className="wt-mono" style={{ color: 'var(--text-muted)', padding: '4px 2px' }}>
              no series — nothing has reported this yet
            </div>
          ) : (
            <div className="wt-table-wrap">
              <table className="wt-table">
                <tbody>
                  {family.series.map((series) => (
                    <tr key={JSON.stringify(series.labels)}>
                      <td className="wt-mono">
                        {Object.entries(series.labels)
                          .filter(([, value]) => value !== '')
                          .map(([key, value]) => `${key}="${value}"`)
                          .join(' ') || '(no labels)'}
                      </td>
                      <td className="wt-mono" style={{ width: 180, textAlign: 'right' }}>
                        {series.value}
                        {family.name === 'watch_check_status' && (
                          <span style={{ color: 'var(--text-muted)' }}>
                            {' '}
                            {STATUS_WORDS[series.value] ?? ''}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {/* `total` is the server's real count; falling back to what
                      arrived keeps this honest against a server that predates
                      the cap, mid-deploy. */}
                  {(family.total ?? family.series.length) > family.series.length && (
                    <tr>
                      <td className="wt-mono" colSpan={2} style={{ color: 'var(--text-muted)' }}>
                        +{(family.total ?? family.series.length) - family.series.length} more series
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ))}
    </>
  );
}
